/**
 * redirectsSlice — Redux Toolkit slice for admin URL redirect management.
 *
 * State shape
 * -----------
 * items: RedirectDTO[]        — current page of redirects
 * total / page / perPage      — pagination meta
 * isLoading: boolean          — list fetch in progress
 * listError: string | null    — last list error
 * loading: per-op booleans
 * error: per-op string | null
 *
 * Thunks: fetchRedirects, createRedirect, updateRedirect, toggleRedirect,
 *         deleteRedirect (bulk import is handled directly in the view).
 *
 * Per `.claude/rules/frontend-data-flow.md` §1, capsules NEVER import
 * this slice; only page-level containers do.
 *
 * CON-SEO-002.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  createAdminRedirect,
  deleteAdminRedirect,
  fetchAdminRedirects,
  updateAdminRedirect,
} from '@dfq/shared/api/admin/redirects';
import { isApiError } from '@dfq/shared/api/_envelope';
import type {
  FetchRedirectsParams,
  RedirectCreatePayload,
  RedirectDTO,
  RedirectUpdatePayload,
} from '@dfq/shared/types/redirects';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export type RedirectLoadingKey =
  | 'createRedirect'
  | 'updateRedirect'
  | 'toggleRedirect'
  | 'deleteRedirect';

export interface RedirectsState {
  items: RedirectDTO[];
  total: number;
  page: number;
  perPage: number;
  lastPage: number;
  isLoading: boolean;
  listError: string | null;
  loading: Record<RedirectLoadingKey, boolean>;
  error: Record<RedirectLoadingKey, string | null>;
}

const initialState: RedirectsState = {
  items: [],
  total: 0,
  page: 1,
  perPage: 25,
  lastPage: 1,
  isLoading: false,
  listError: null,
  loading: {
    createRedirect: false,
    updateRedirect: false,
    toggleRedirect: false,
    deleteRedirect: false,
  },
  error: {
    createRedirect: null,
    updateRedirect: null,
    toggleRedirect: null,
    deleteRedirect: null,
  },
};

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const fetchRedirects = createAsyncThunk(
  'redirects/fetchRedirects',
  async (params: FetchRedirectsParams = {}, { rejectWithValue }) => {
    try {
      return await fetchAdminRedirects(params);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load redirects';
      return rejectWithValue(message);
    }
  },
);

export const createRedirect = createAsyncThunk(
  'redirects/createRedirect',
  async (payload: RedirectCreatePayload, { rejectWithValue }) => {
    try {
      return await createAdminRedirect(payload);
    } catch (err) {
      if (isApiError(err) && err.errors) {
        return rejectWithValue({ errors: err.errors, message: err.message });
      }
      const message = err instanceof Error ? err.message : 'Failed to create redirect';
      return rejectWithValue({ message });
    }
  },
);

export const updateRedirect = createAsyncThunk(
  'redirects/updateRedirect',
  async (
    { id, payload }: { id: number; payload: RedirectUpdatePayload },
    { rejectWithValue },
  ) => {
    try {
      return await updateAdminRedirect(id, payload);
    } catch (err) {
      if (isApiError(err) && err.errors) {
        return rejectWithValue({ errors: err.errors, message: err.message });
      }
      const message = err instanceof Error ? err.message : 'Failed to update redirect';
      return rejectWithValue({ message });
    }
  },
);

export const toggleRedirect = createAsyncThunk(
  'redirects/toggleRedirect',
  async (
    { id, isActive }: { id: number; isActive: boolean },
    { rejectWithValue, getState },
  ) => {
    const state = (getState() as { redirects: RedirectsState }).redirects;
    const current = state.items.find((r) => r.id === id);
    if (!current) {
      return rejectWithValue('Redirect not found in state');
    }
    try {
      return await updateAdminRedirect(id, {
        source_path: current.sourcePath,
        target_path: current.targetPath,
        status_code: current.statusCode,
        is_active: isActive,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update redirect';
      return rejectWithValue(message);
    }
  },
);

export const deleteRedirect = createAsyncThunk(
  'redirects/deleteRedirect',
  async (id: number, { rejectWithValue }) => {
    try {
      await deleteAdminRedirect(id);
      return id;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete redirect';
      return rejectWithValue(message);
    }
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const redirectsSlice = createSlice({
  name: 'redirects',
  initialState,
  reducers: {
    // Optimistic is_active toggle — reverted via patchRedirect.rejected.
    optimisticToggleActive(state, action: { payload: { id: number; isActive: boolean } }) {
      const item = state.items.find((r) => r.id === action.payload.id);
      if (item) {
        item.isActive = action.payload.isActive;
      }
    },
  },
  extraReducers: (builder) => {
    // fetchRedirects
    builder
      .addCase(fetchRedirects.pending, (state) => {
        state.isLoading = true;
        state.listError = null;
      })
      .addCase(fetchRedirects.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload.redirects;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.perPage = action.payload.perPage;
        state.lastPage = action.payload.lastPage;
      })
      .addCase(fetchRedirects.rejected, (state, action) => {
        state.isLoading = false;
        state.listError = (action.payload as string) ?? 'Failed to load redirects';
      });

    // createRedirect
    builder
      .addCase(createRedirect.pending, (state) => {
        state.loading.createRedirect = true;
        state.error.createRedirect = null;
      })
      .addCase(createRedirect.fulfilled, (state, action) => {
        state.loading.createRedirect = false;
        state.items.unshift(action.payload);
        state.total += 1;
      })
      .addCase(createRedirect.rejected, (state, action) => {
        state.loading.createRedirect = false;
        const payload = action.payload as { message?: string } | undefined;
        state.error.createRedirect = payload?.message ?? 'Failed to create redirect';
      });

    // updateRedirect
    builder
      .addCase(updateRedirect.pending, (state) => {
        state.loading.updateRedirect = true;
        state.error.updateRedirect = null;
      })
      .addCase(updateRedirect.fulfilled, (state, action) => {
        state.loading.updateRedirect = false;
        const idx = state.items.findIndex((r) => r.id === action.payload.id);
        if (idx !== -1) state.items[idx] = action.payload;
      })
      .addCase(updateRedirect.rejected, (state, action) => {
        state.loading.updateRedirect = false;
        const payload = action.payload as { message?: string } | undefined;
        state.error.updateRedirect = payload?.message ?? 'Failed to update redirect';
      });

    // toggleRedirect (optimistic — revert on rejection)
    builder
      .addCase(toggleRedirect.pending, (state) => {
        state.loading.toggleRedirect = true;
        state.error.toggleRedirect = null;
      })
      .addCase(toggleRedirect.fulfilled, (state, action) => {
        state.loading.toggleRedirect = false;
        const idx = state.items.findIndex((r) => r.id === action.payload.id);
        if (idx !== -1) state.items[idx] = action.payload;
      })
      .addCase(toggleRedirect.rejected, (state, action) => {
        state.loading.toggleRedirect = false;
        state.error.toggleRedirect = (action.payload as string) ?? 'Failed to toggle redirect';
      });

    // deleteRedirect
    builder
      .addCase(deleteRedirect.pending, (state) => {
        state.loading.deleteRedirect = true;
        state.error.deleteRedirect = null;
      })
      .addCase(deleteRedirect.fulfilled, (state, action) => {
        state.loading.deleteRedirect = false;
        state.items = state.items.filter((r) => r.id !== action.payload);
        state.total = Math.max(0, state.total - 1);
      })
      .addCase(deleteRedirect.rejected, (state, action) => {
        state.loading.deleteRedirect = false;
        state.error.deleteRedirect = (action.payload as string) ?? 'Failed to delete redirect';
      });
  },
});

export const { optimisticToggleActive } = redirectsSlice.actions;
export const redirectsReducer = redirectsSlice.reducer;
